<!-- 講座会員数、月額、今すぐ入会ボタンを表示する -->
<script setup lang="ts">
  import { usePlanJoinStore } from "~/stores/PlanJoinStore";
  import { useSnackBarStore } from "~/stores/SnackBarStore";
  import { usePlanPriceChangeReservationStore } from "~/stores/PlanPriceChangeReservationStore";
  import { useHistoryStore } from "~/stores/HistoryStore";
  import { PlanInfo, type IPlanPrice } from "~/entities/PlanInfo";

  interface Props {
    planInfo: PlanInfo;
  }
  const props = withDefaults(defineProps<Props>(), {
    planInfo: undefined,
  });

  const historyStore = useHistoryStore();

  // TODO: 料金プラン変更機能の初回リリースでは、一括料金プランへの変更機能を無効にする。
  const disableChangeToOnlyOnce = true;

  // 講座入会情報store
  const planJoinStore = usePlanJoinStore();

  // スナックバーstore
  const snackBarStore = useSnackBarStore();

  // 講座料金変更予約store
  const planPriceChangeReservationStore = usePlanPriceChangeReservationStore();

  // 変更確認ダイアログ用
  const isChangePriceDialogOpened = ref(false);
  const changePlanPriceId = ref("");

  // ロード中かどうか
  const isLoading = ref(true);

  // 講座入会へ
  const onClickJoin = async (planPriceId: string) => {
    const planId = props.planInfo.id;

    // 講座入会情報を init
    const initResult = await planJoinStore.init(planId, planPriceId);
    if (initResult) {
      // 講座入会フローを開始
      historyStore.to({
        path: `/plan/${planId}/join/${planJoinStore.firstStatus()}`,
        query: { planPriceId: planPriceId.toString() },
      });
    } else {
      // 入会済みなので、講座概要に戻りその旨を表示
      snackBarStore.set("この講座は入会済みです");
      historyStore.to(`/plan/${planId}`, { replace: true });
    }
  };

  // 支払い情報へ
  const onClickPayment = () => {
    const planId = props.planInfo.id;
    historyStore.to(`/plan/${planId}/payment`);
  };

  // 料金プラン変更
  const onClickChangePrice = (planPriceId: string) => {
    isChangePriceDialogOpened.value = true;
    changePlanPriceId.value = planPriceId;
  };

  // 料金プラン変更ダイアログOk
  const onClickChangePriceYes = async () => {
    const planId = props.planInfo.id;
    const planPriceId = changePlanPriceId.value;

    // 講座入会情報を init
    const initResult = await planJoinStore.init(planId, planPriceId, undefined, true);
    if (initResult) {
      // 講座入会フローを開始
      historyStore.to({
        path: `/plan/${planId}/join/${planJoinStore.firstStatus()}`,
        query: { planPriceId: planPriceId.toString(), isChange: "true" },
      });
    } else {
      // 入会済みなので、講座概要に戻りその旨を表示
      snackBarStore.set("この講座は入会済みです");
    }
  };

  const playTypeText = (planPrice: any) => {
    if (planPrice.billingType === "contract_period") {
      if (planPrice.contractPeriod > 1) {
        return `${planPrice.contractPeriod}ヶ月ごと`;
      }
      return "月額";
    }
    return "一括購入";
  };

  const planExpireText = (planPrice: any) => {
    if (planPrice.billingType === "contract_period") {
      return `更新確定日（決済日）：${FormatDateString(props.planInfo?.contractStatus.nextSettlementDate, "yyyy年MM月dd日")}`;
    }
    return `${FormatDateString(props.planInfo?.contractStatus.expirationDate, "yyyy年MM月dd日")}まで閲覧可能`;
  };

  const priceList = computed(() => {
    return props.planInfo?.planPriceList.filter(planPrice => {
      // 契約中のものは表示する
      if (props.planInfo?.contractStatus.planPriceId === planPrice.planPriceId) {
        return true;
      }
      // 変更予約中のものは表示する
      if (planPriceChangeReservationStore.item?.changeReservationPlanPriceId === planPrice.planPriceId) {
        return true;
      }
      // 銀行振込申込中のものは表示する
      if (props.planInfo?.waitingBankTransfers?.some(item => item.planPriceId === String(planPrice.planPriceId))) {
        return true;
      }
      // isHiddenがtrueのものは表示しない
      if (planPrice.isHidden) {
        return false;
      }
      // trialDaysが0以上のもので、過去に契約したことがあるものは表示しない
      if ((planPrice.trialDays || 0) > 0 && planPrice.isContractedInPast) {
        return false;
      }
      return true;
    });
  });

  const getWaitingBankTransfer = (planPriceId: string) => {
    return props.planInfo?.waitingBankTransfers
      .map(item => {
        return {
          planPriceId: item.planPriceId,
          dueDate: FormatDateString(item.dueDate, "yyyy年MM月dd日"),
        };
      })
      .find(item => item.planPriceId === planPriceId);
  };

  const availableChangePrice = (planPrice: IPlanPrice) => {
    // 現在契約中の料金プランが一括の場合は変更できない
    if (props.planInfo?.contractStatus?.billingType === "only_once") {
      return false;
    }
    // 他に予約中のプランがある場合は何もできない
    if (planPriceChangeReservationStore.item?.changeReservationPlanPriceId) {
      return false;
    }
    // 入会中以外のステータスの場合は変更できない
    if (props.planInfo?.contractStatus?.contractStatusCode !== "active") {
      return false;
    }
    // 一括への変更はできない
    if (disableChangeToOnlyOnce && planPrice.billingType === "only_once") {
      return false;
    }
    return true;
  };

  onMounted(() => {
    try {
      planPriceChangeReservationStore.fetch(props.planInfo.id);
    } catch (error: any) {
      sentryErrorLog(`planPriceChangeReservationStore.fetch error : ${error}`);
    } finally {
      // 即isLoadingをfalseにするとチラつくため少し遅延させる
      setTimeout(() => {
        isLoading.value = false;
      }, 100);
    }
  });
</script>

<template>
  <template v-if="!isLoading">
    <!-- 料金プランごとに表示 -->
    <div
      v-for="(planPrice, i) in priceList"
      :key="i"
      class="d-flex mb-4 pa-4 flex-column align-center rounded bg-monochrome-04"
    >
      <div v-if="(planPrice.trialDays || 0) > 0" class="w-100">
        <div class="campaign">
          <div class="text-center">{{ planPrice.priceName }}</div>
          <div class="text-center mb-2 text-red">
            <span class="text-h5 font-weight-bold">{{ planPrice.trialDays }}日間無料</span>
          </div>
          <!-- <div class="text-center">
            {{ FormatDateString(planPrice.endTrialDays, "yyyy年MM月dd日") || "-" }}まで
          </div> -->
        </div>
        <div class="text-center my-2">そのあと</div>
      </div>
      <div v-else class="w-100">
        <div class="plan-price-name">
          <div class="text-center">{{ planPrice.priceName }}</div>
        </div>
      </div>

      <p class="plan-price-price mb-3 mb-sm-0">
        <span class="plan-price-price-month">{{ playTypeText(planPrice) }}￥</span>
        <span class="plan-price-price-number">{{ numberFormat(planPrice.price) }} </span>
        <span class="plan-price-tax">(税込)</span>
      </p>
      <div class="plan-price-note">記載の料金に加えて別途5%の決済手数料がかかります。</div>

      <!-- 入会可能ではない場合 -->
      <AtomsPlanJoinButton v-if="!planPrice.isExpire" disabled text="入会停止中" />
      <!-- 未入会の場合 -->
      <template v-else-if="planInfo?.contractStatus.contractStatusCode === 'no_contract'">
        <!-- 入会上限に達している場合 -->
        <AtomsPlanJoinButton
          v-if="planInfo.planMemberNumberLimit > 0 && planInfo.planMemberNumber >= planInfo.planMemberNumberLimit"
          disabled
          text="定員に達しました"
        />
        <div v-else>
          <!-- 銀行振込申込中の場合 -->
          <div v-if="!!getWaitingBankTransfer(String(planPrice.planPriceId))">
            <div class="mt-2">
              <div class="text-center font-weight-bold text-h6 mb-1">申し込み中</div>
              <div class="text-center text-red">
                支払い期限 : {{ getWaitingBankTransfer(String(planPrice.planPriceId))?.dueDate }}まで
              </div>
            </div>
            <AtomsPlanJoinButton
              :click="() => onClickJoin(planPrice.planPriceId.toString())"
              color="main-01"
              text="支払い方法を変更する"
            />
          </div>
          <!-- 通常の入会ボタン -->
          <AtomsPlanJoinButton v-else :click="() => onClickJoin(planPrice.planPriceId.toString())" />
        </div>
      </template>
      <!-- 入会中の場合 -->
      <template v-else>
        <!-- 入会中の料金プランである場合 -->
        <template v-if="planInfo?.contractStatus.planPriceId === planPrice.planPriceId">
          <!-- 入会中/有効契約の場合 -->
          <div v-if="planInfo?.contractStatus.contractStatusCode === 'active'" class="mt-2">
            <div class="plan-price-already">講座入会中</div>
            <div class="plan-price-already-note">
              {{ planExpireText(planPrice) }}
            </div>
          </div>
          <!-- 入会中/解約予定の場合 -->
          <div v-else-if="planInfo?.contractStatus.contractStatusCode === 'cancellation'" class="mt-2">
            <div v-if="compareWithCurrentDate(planInfo!.contractStatus.expirationDate)" class="my-2">
              <AtomsContractCancelButton :plan-id="planInfo.id" />
            </div>
            <div class="plan-price-already">講座解約済み</div>
            <div v-if="planPrice.isSubscriptionFlag" class="plan-price-already-note">
              {{ FormatDateString(planInfo?.contractStatus.expirationDate, "yyyy年MM月dd日") }}まで閲覧可能
            </div>
          </div>
          <!-- 入会中/仮解約中の場合 -->
          <div
            v-else-if="planInfo?.contractStatus.contractStatusCode === 'provisional_cancellation'"
            class="d-flex flex-column align-center mt-2"
          >
            <div class="plan-price-note text-main-04">講座の仮解約中です。支払い情報を更新してください。</div>
            <AtomsPlanJoinButton text="支払い情報を更新する" @click="onClickPayment" />
          </div>
        </template>
        <!-- 入会中の料金プランでない場合、かつ料金プラン変更機能ONの場合はプラン変更系のボタンを表示する -->
        <template v-else>
          <!-- 現在契約中の料金プランが一括の場合は変更できないようにする -->
          <AtomsPlanJoinButton v-if="planInfo?.contractStatus.billingType === 'only_once'" disabled />
          <!-- 変更予約中の料金プランの場合 -->
          <template
            v-else-if="planPriceChangeReservationStore.item?.changeReservationPlanPriceId === planPrice.planPriceId"
          >
            <AtomsPlanJoinButton disabled text="変更予約済み" />
            <div class="text-center plan-price-change-note">※入会中プラン更新時に決済と入会が完了します</div>
          </template>
          <!-- 銀行振込申込中の場合 -->
          <div v-else-if="!!getWaitingBankTransfer(String(planPrice.planPriceId))">
            <div class="mt-2">
              <div class="text-center font-weight-bold text-h6 mb-1">変更申し込み中</div>
              <div class="text-center text-red">
                支払い期限 : {{ getWaitingBankTransfer(String(planPrice.planPriceId))?.dueDate }}まで
              </div>
            </div>
            <AtomsPlanJoinButton
              :click="() => onClickChangePrice(planPrice.planPriceId.toString())"
              color="main-01"
              text="支払い方法を変更する"
            />
          </div>
          <!-- 変更予約中の料金プラン以外 -->
          <template v-else>
            <!-- 他に予約中のプランがない場合は予約ボタン -->
            <AtomsPlanJoinButton
              v-if="availableChangePrice(planPrice)"
              text="プラン変更予約"
              :click="() => onClickChangePrice(planPrice.planPriceId.toString())"
            />
            <AtomsPlanJoinButton v-else disabled />
          </template>
        </template>
      </template>

      <!-- 料金プラン変更ダイアログ -->
      <MoleculesYesNoDialog
        v-model:is-open="isChangePriceDialogOpened"
        message="プラン変更予約について"
        sub-message="現在のプランは更新日までそのまま利用できます。
次の更新日から新しいプランに切り替わり、その時点で新しいプランの料金をお支払いいただきます。"
        sub-message-direction="left"
        yes-title="プラン変更予約へ進む"
        yes-color="main-04"
        :hide-no-button="true"
        @click-yes="onClickChangePriceYes"
      />
    </div>
  </template>
</template>

<style lang="scss" scoped>
  @import "assets/media.scss";

  .plan-price-part {
    background-color: rgb(var(--v-theme-monochrome-04));
  }
  .plan-price-price {
    .plan-price-price-month {
      font-size: 20px;
      font-weight: 700;
      line-height: 1.8em;
      letter-spacing: 0.06em;
    }
    .plan-price-price-number {
      font-size: 32px;
      font-weight: 700;
      line-height: 1.8em;
      letter-spacing: 0em; /*行間を狭める必要あり*/
    }
    .plan-price-tax {
      font-size: 12px;
      font-weight: 400;
      line-height: 1.8em;
      letter-spacing: 0.06em;
    }
  }
  .plan-price-note {
    font-size: 12px;
    font-weight: 400;
    text-align: center;
    line-height: 1.8em;
    letter-spacing: 0em; /*行間を狭める必要あり*/
    color: #aaaaaa;
  }
  .plan-price-change-note {
    font-size: 12px;
    font-weight: 400;
    text-align: center;
    line-height: 1.8em;
    letter-spacing: 0em; /*行間を狭める必要あり*/
  }
  .plan-price-already {
    font-size: 20px;
    font-weight: 700;
    line-height: 1.8em;
    letter-spacing: 0.06em;
    text-align: center;
  }
  .plan-price-already-note {
    font-size: 16px;
    font-weight: 400;
    line-height: 1.8em;
    letter-spacing: 0.06em;
    text-align: center;
  }
  .plan-provisonal-cancellation-btn {
    font-size: 16px;
    font-weight: 700;
    line-height: 1.8em;
    letter-spacing: 0.06em;
    text-align: center;
  }
  .campaign {
    width: 90%;
    margin: 0 auto;
    border-top: 1px solid rgb(var(--v-theme-monochrome-03));
    border-bottom: 1px solid rgb(var(--v-theme-monochrome-03));
    padding: 10px 0;
  }
  .plan-price-name {
    width: 90%;
    margin: 0 auto;
    border-top: 1px solid rgb(var(--v-theme-monochrome-03));
    border-bottom: 1px solid rgb(var(--v-theme-monochrome-03));
    padding: 10px 0;
    font-size: 16px;
    font-weight: 400;
    line-height: 1.8em;
    letter-spacing: 0.06em;
  }
</style>
